import { FC, useEffect, useRef, useState } from "react";
import { Box, Link, Typography, IconButton } from "@mui/material";
import OtpInput from "@/components/UI/OtpInput/OtpInput";
import theme from "@/theme";
import logoImg from "@/assets/image/logo.png";
import ArrowIcon from "@/assets/svg/ArrowIcon";
type Props = {
  email: string;
  length?: number;
  onSubmit?: (otpCode: string) => void | Promise<void>;
  onResend?: () => void | Promise<void>;
  onHeaderClick: () => void;
};

const OtpFormModal: FC<Props> = ({ email, length = 6, onSubmit, onResend, onHeaderClick }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [errorOtp, setErrorOtp] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (otp.join("").length === length) {
      (async () => {
        try {
          setErrorOtp(false);
          if (onSubmit) await onSubmit(otp.join(""));
        } catch {
          setErrorOtp(true);
        }
      })();
    }
  }, [otp]);

  useEffect(() => {
    if (inputsRef.current[0]) inputsRef.current[0].focus();
  }, []);

  useEffect(() => {
    if (errorOtp && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [errorOtp]);

  useEffect(() => {
    if (!resendCooldown) return;
    const interval = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").toUpperCase();
    const pasteChars = pasteData.split("").filter((c) => /^[0-9A-Z]$/.test(c));
    if (pasteChars.length >= length) {
      const newOtp = [...otp];
      for (let i = 0; i < length; i++) newOtp[i] = pasteChars[i] || "";
      setOtp(newOtp);
    }
  };

  const resendOtp = async () => {
    setOtp(Array(length).fill(""));
    setErrorOtp(false);
    setResendError(null);

    try {
      if (onResend) await onResend();
      setResendCooldown(60);
    } catch (e: any) {
      setResendError(e?.message || "Something went wrong");
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 0 40px",
        borderRadius: "24px",
        width: "520px",
        // height: "464px",
        boxShadow: "0 0 25px rgba(22,22,22,0.05)",
        bgcolor: theme.palette.custom.mainWhite,
        fontFamily: "Inter, sans-serif",
        color: "#2A2D31",
        boxSizing: "border-box",
        outline: "none",
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: "10px",
          mb: "32px",
        }}
      >
        <IconButton
          onClick={onHeaderClick}
          sx={{
            position: "absolute",
            left: 0,
            color: "#52575D",
            ml: "16px",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          <ArrowIcon size={24} color="#383C40" direction="left" />
        </IconButton>

        <img src={logoImg} alt="Logo" width={115} height={36} style={{ objectFit: "contain" }} />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "390px",
          height: "104px",
          mb: "52px",
        }}
      >
        <Typography
          component="h1"
          variant="h2"
          fontSize="22px"
          fontWeight={600}
          lineHeight="118%"
          textAlign="center"
          color={theme.palette.custom.mainBlack}
          sx={{ mb: "6px" }}
        >
          Verify your email
        </Typography>

        <Typography
          variant="body1"
          fontSize="15px"
          fontWeight={500}
          lineHeight="160%"
          textAlign="center"
          color={theme.palette.custom.blueGray600}
          sx={{ mb: 4 }}
        >
          We sent you a six digit confirmation code to
          <Typography
            fontSize="15px"
            fontWeight={500}
            component="span"
            display="block"
            color={theme.palette.custom.blueGray800}
            sx={{ mt: 0.5, mb: 0.5 }}
          >
            {email}
          </Typography>
          Please enter it below to confirm your email address.
        </Typography>
      </Box>
      <Box
        sx={{
          width: "391px",
          height: "180px",
        }}
      >
        <OtpInput
          label="Confirmation code"
          length={length}
          value={otp}
          error={errorOtp}
          helperText={errorOtp ? "Incorrect OTP" : (resendError ?? undefined)}
          onChange={(newOtp) => {
            setOtp(newOtp);
            setErrorOtp(false);
          }}
          onPaste={handlePaste}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
            height: "24px",
            mt: "72px",
          }}
        >
          <Typography
            sx={{}}
            fontSize="15px"
            fontWeight={500}
            lineHeight="24px"
            textAlign="center"
            color="#52575D"
          >
            Didn't receive a code?{" "}
            {resendCooldown > 0 ? (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                <Link
                  underline="always"
                  sx={{
                    color: theme.palette.custom.blueGray900,
                    cursor: "not-allowed",
                    "&:hover": { color: "#B0B5BA" },
                    fontWeight: 600,
                    fontSize: "15px",
                    lineHeight: "22px",
                  }}
                >
                  Send code again ({formatTime(resendCooldown)})
                </Link>
              </Box>
            ) : (
              <Link
                underline="always"
                onClick={resendOtp}
                sx={{
                  color: theme.palette.custom.mainBlue,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                Send code again
              </Link>
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OtpFormModal;
